import { cosemApi } from '@services/providers/cosem';
import { ApiPaginationResponse, ApiResponse } from '@services/providers/cosem.types';
import {
    FinancialBenefit,
    FinancialBenefitToCreate,
    FinancialBenefitToUpdate,
    FinancialBenefitListItem
} from '@services/member/list/financial-benefits/financial-benefits.types';
import {
    FinancialBenefit as MemberFinancialBenefit
} from '@services/member/member/member.types';
import { ActiveFilter } from '@utils/filters';

const financialBenefitsApi = cosemApi.injectEndpoints({
    endpoints: (build) => ({
        getFinancialBenefits: build.mutation<ApiResponse<FinancialBenefitListItem[]>, void>({
            query: () => {
                return {
                    url: `/member/financial-benefits`,
                    method: 'GET'
                };
            },
            transformResponse: (response: ApiResponse<FinancialBenefitListItem[]>) => response
        }),
        getPaginatedFinancialBenefits: build.mutation<
            ApiPaginationResponse<FinancialBenefit>,
            { limit?: number; page?: number; search?: string; orderBy?: string }
        >({
            query: ({ limit = 10, page = 1, search, orderBy }) => {
                return {
                    url: `/member/financial-benefits/paginated`,
                    method: 'GET',
                    params: { limit, page, search: search ?? '', orderBy: orderBy ?? '' }
                };
            },
            transformResponse: (response: ApiPaginationResponse<FinancialBenefit>) => response
        }),
        getArchivedPaginatedFinancialBenefits: build.mutation<
            ApiPaginationResponse<FinancialBenefit>,
            { limit?: number; page?: number; search?: string; orderBy?: string }
        >({
            query: ({ limit = 10, page = 1, search, orderBy }) => {
                return {
                    url: `/member/financial-benefits/archived/paginated`,
                    method: 'GET',
                    params: { limit, page, search: search ?? '', orderBy: orderBy ?? '' }
                };
            },
            transformResponse: (response: ApiPaginationResponse<FinancialBenefit>) => response
        }),
        updateFinancialBenefit: build.mutation<ApiResponse<FinancialBenefit>, FinancialBenefitToUpdate>({
            query: (financialBenefit: FinancialBenefitToUpdate) => {
                return {
                    url: `/member/financial-benefits/${financialBenefit.id}`,
                    method: 'PUT',
                    body: financialBenefit
                };
            },
            transformResponse: (response: ApiResponse<FinancialBenefit>) => response
        }),
        createFinancialBenefit: build.mutation<ApiResponse<FinancialBenefit>, FinancialBenefitToCreate>({
            query: (financialBenefit: FinancialBenefitToCreate) => {
                return {
                    url: `/member/financial-benefits`,
                    method: 'POST',
                    body: financialBenefit
                };
            },
            transformResponse: (response: ApiResponse<FinancialBenefit>) => response
        }),
        deleteFinancialBenefit: build.mutation<ApiResponse<FinancialBenefit>, { id: string }>({
            query: ({ id }) => {
                return {
                    url: `/member/financial-benefits/${id}`,
                    method: 'DELETE'
                };
            },
            transformResponse: (response: ApiResponse<FinancialBenefit>) => response
        }),
        toggleArchiveFinancialBenefit: build.mutation<ApiResponse<FinancialBenefit>, { id: string|number, archived: boolean }>({
            query: ({ id, archived }) => {
                return { 
                    url: `/member/financial-benefits/${id}/archive`, 
                    method: 'PUT',
                    body: { archived }
                };
            },
            transformResponse: (response: ApiResponse<FinancialBenefit>) => response
        }),
        getFinancialBenefitsEdition: build.mutation<
            ApiPaginationResponse<MemberFinancialBenefit>,
            { limit?: number; page?: number; search?: string; orderBy?: string; filters?: ActiveFilter[] }
        >({
            query: ({ limit = 10, page = 1, search, orderBy, filters }) => {
                return {
                    url: `/member/financial-benefits/edition`,
                    method: 'GET',
                    params: { limit, page, search: search ?? '', orderBy: orderBy ?? '', filters: JSON.stringify(filters ?? []) }
                };
            },
            transformResponse: (response: ApiPaginationResponse<MemberFinancialBenefit>) => response
        }),
        getFinancialBenefitsExport: build.mutation<ApiResponse<string>, {search?: string; orderBy?: string; filters?: ActiveFilter[] }>({
            query: ({ search, orderBy, filters }) => {
                return {
                    url: `/member/financial-benefits/edition/export`,
                    method: 'GET',
                    params: { search: search ?? '', orderBy: orderBy ?? '', filters: JSON.stringify(filters ?? []) }
                };
            },
            transformResponse: (response: ApiResponse<string>) => response
        }),
    })
});

export const {
    useGetFinancialBenefitsMutation,
    useGetPaginatedFinancialBenefitsMutation,
    useGetArchivedPaginatedFinancialBenefitsMutation,
    useUpdateFinancialBenefitMutation,
    useCreateFinancialBenefitMutation,
    useDeleteFinancialBenefitMutation,
    useToggleArchiveFinancialBenefitMutation,
    useGetFinancialBenefitsEditionMutation,
    useGetFinancialBenefitsExportMutation
} = financialBenefitsApi;
